import {
  FieldPath,
  Timestamp,
  arrayUnion,
  deleteField,
  doc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { applyBracketResult } from '../lib/bracket';
import { bracketFor, canWithdrawFrom, withoutParticipants } from '../lib/stages';
import { makeSubcollection } from './subcollection';

// Stages of a staged tournament, at `tournaments/{id}/stages`. Each doc holds
// the stage settings, its participants (seed order) and its results; see
// src/lib/stages.js for the shape and the rules that run over it.
const stages = makeSubcollection('stages');

const now = () => Timestamp.now();

// Live, ordered stage list. Returns the unsubscribe function.
export const subscribeStages = (tournamentId, onData, onError) =>
  onSnapshot(
    query(stages.col(tournamentId), orderBy('order')),
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onError
  );

// Writes the first stages of a new tournament in one go.
export const createStages = async (tournamentId, list) => {
  const batch = writeBatch(db);
  list.forEach((stage, order) => {
    batch.set(doc(stages.col(tournamentId)), {
      ...stage,
      order,
      createdAt: now(),
      updatedAt: now(),
    });
  });
  await batch.commit();
};

// `stage.order` must be set by the caller (append = current stage count).
export const addStage = async (tournamentId, stage) =>
  (await stages.create(tournamentId, stage)).id;

export const updateStage = (tournamentId, stageId, patch) =>
  stages.update(tournamentId, stageId, patch);

// Deletes a stage and closes the gap in `order`. `list` is the current
// ordered stage list. The new first stage takes everyone, so it loses its
// advancement count.
export const deleteStage = async (tournamentId, list, stageId) => {
  const batch = writeBatch(db);
  batch.delete(stages.ref(tournamentId, stageId));
  list
    .filter((s) => s.id !== stageId)
    .forEach((s, order) => {
      const patch = {};
      if (s.order !== order) patch.order = order;
      if (order === 0 && s.advanceCount !== null) patch.advanceCount = null;
      if (Object.keys(patch).length) {
        batch.update(stages.ref(tournamentId, s.id), { ...patch, updatedAt: now() });
      }
    });
  await batch.commit();
};

// Swaps stage `index` with its neighbour (`direction` -1 = earlier, 1 = later).
export const moveStage = async (tournamentId, list, index, direction) => {
  const a = list[index];
  const b = list[index + direction];
  if (!a || !b) return;
  const batch = writeBatch(db);
  const place = (stage, order) => ({
    order,
    // Only the first stage has no advancement count; give a stage leaving
    // the first slot a sensible default.
    advanceCount: order === 0 ? null : (stage.advanceCount ?? 8),
    updatedAt: now(),
  });
  batch.update(stages.ref(tournamentId, a.id), place(a, b.order));
  batch.update(stages.ref(tournamentId, b.id), place(b, a.order));
  await batch.commit();
};

export const startStage = (tournamentId, stageId, participants) =>
  stages.update(tournamentId, stageId, { status: 'active', participants });

// Late entries into a running leaderboard stage.
export const addStageParticipants = (tournamentId, stageId, playerIds) =>
  stages.update(tournamentId, stageId, { participants: arrayUnion(...playerIds) });

// One leaderboard cell. `value` is ms (time) or points; null clears it.
export const setLeaderboardValue = (tournamentId, stageId, playerId, categoryId, value) =>
  updateDoc(
    stages.ref(tournamentId, stageId),
    new FieldPath('results', playerId, categoryId),
    value === null ? deleteField() : value,
    'updatedAt',
    now()
  );

// Sets or clears (`result = null`) one bracket match. Runs in a transaction so
// two admins entering results at once can't overwrite each other. Resolves to
// the ids of later matches whose results had to be cleared.
export const setBracketResult = (tournamentId, stageId, matchId, result) =>
  runTransaction(db, async (tx) => {
    const ref = stages.ref(tournamentId, stageId);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('This stage no longer exists.');
    const stage = snap.data();
    if (stage.status !== 'active') throw new Error('This stage is not running.');
    const { results, reset } = applyBracketResult(
      bracketFor(stage),
      stage.participants || [],
      stage.results || {},
      matchId,
      result
    );
    tx.update(ref, { results, updatedAt: now() });
    return reset;
  });

// Freezes the standings and seeds the next stage (`next` = { id, participants }
// or null for the last stage).
export const completeStage = async (tournamentId, stageId, standings, next) => {
  const batch = writeBatch(db);
  batch.update(stages.ref(tournamentId, stageId), {
    status: 'completed',
    standings,
    updatedAt: now(),
  });
  if (next) {
    batch.update(stages.ref(tournamentId, next.id), {
      participants: next.participants,
      updatedAt: now(),
    });
  }
  await batch.commit();
};

// Undo a completion while the next stage has not started yet.
export const reopenStage = async (tournamentId, stageId, nextId) => {
  const batch = writeBatch(db);
  batch.update(stages.ref(tournamentId, stageId), {
    status: 'active',
    standings: deleteField(),
    updatedAt: now(),
  });
  if (nextId) {
    batch.update(stages.ref(tournamentId, nextId), { participants: [], updatedAt: now() });
  }
  await batch.commit();
};

// Back to setup: every result in the stage is deleted, participants stay.
export const resetStage = (tournamentId, stageId) =>
  stages.update(tournamentId, stageId, {
    status: 'pending',
    results: {},
    standings: deleteField(),
  });

// Takes players out of one stage: participants, results and (completed
// stage) standings. Refuses a running or finished bracket.
export const removeStageParticipants = async (tournamentId, stage, playerIds) => {
  if (playerIds.some((id) => !canWithdrawFrom(stage, id))) {
    throw new Error(`Players can't leave ${stage.name} once its bracket has started.`);
  }
  const patch = withoutParticipants(stage, playerIds);
  if (!patch) return;
  await updateDoc(stages.ref(tournamentId, stage.id), { ...patch, updatedAt: now() });
};

// Deletes a roster entry and takes it out of every stage in the same batch,
// so no stage is left showing a "removed player". `list` = current stages.
export const deletePlayerEverywhere = async (tournamentId, playerRef, list) => {
  const blocking = list.find((s) => !canWithdrawFrom(s, playerRef.id));
  if (blocking) {
    throw new Error(
      `They play in the ${blocking.name} bracket, which has started. Reset that stage first, or keep them in the roster.`
    );
  }
  const batch = writeBatch(db);
  batch.delete(playerRef);
  list.forEach((s) => {
    const patch = withoutParticipants(s, [playerRef.id]);
    if (patch) batch.update(stages.ref(tournamentId, s.id), { ...patch, updatedAt: now() });
  });
  await batch.commit();
};
